import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import classNames from "classnames";
import { Page, Selector } from "@/model";
import selectorRepository from "@/repository/selectorRepository";
import { useIntentNames } from "@/query/intent";
import { useDeleteSelector } from "@/query/selector";
import { editingSelectorAtom } from "@/atom/editing";
import { useAgentId, useDialog, useSelection } from "@/hook";
import HighlightText from "@/component/HighlightText";
import { IndeterminateCheckbox } from "@/component/input";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import SelectionActionBar from "@/component/SelectionActionBar";
import SortButton from "@/component/SortBurtton";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { SelectorPageParam } from "../SelectorView";


interface SelectorTableProps {
    page?: Page<Selector>;
    pageParam: SelectorPageParam;
    onClickSortHead: (key: 'sentence' | 'intent_name') => void;
}

const SelectorTable: React.FC<SelectorTableProps> = (props) => {
    const { page, pageParam, onClickSortHead } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();

    const [editingSelector, setEditingSelector] = useAtom(editingSelectorAtom);
    const { data: intents } = useIntentNames(agentId);
    const { mutateAsync: deleteSelectors } = useDeleteSelector(agentId);

    const pageIds = useMemo(() => page?.content.map(selector => selector.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0
    });

    const deleteByIds = async (ids: string[]) => {
        await deleteSelectors({ ids: ids })
            .then(() => {
                clearSelection();
                if (editingSelector && ids.includes(editingSelector.id)) {
                    setEditingSelector(undefined);
                }
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteSelectors({ keyword: pageParam.searchKeyword })
            .then(() => {
                clearSelection();
                setEditingSelector(undefined);
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onClickTableRowDeleteBtn = (selector: Selector) => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i04019" }),
            content: intl.formatMessage({ id: "i04022" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => deleteByIds([selector.id])
        });
    };

    const onClickActionBarDeleteBtn = (e: React.MouseEvent) => {
        e.preventDefault();

        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i04013" }),
            content: intl.formatMessage({ id: "i04014" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds)
        });
    };

    const intentActiveMap = useMemo(() => {
        if (!intents) {
            return {};
        }
        return Object.fromEntries(
            intents.map(intent => [intent.id, intent.active])
        );
    }, [intents]);

    const onClickDownload = () => {
        if (isAllSelected) {
            selectorRepository.export(agentId, { keyword: pageParam.searchKeyword });
        } else {
            selectorRepository.export(agentId, { ids: selectedIds });
        }
    };

    return (
        <div role="table"
             className="selection-table grid-table-selector"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={checked => markPage(checked)}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th group"
                >
                    <SortButton order={pageParam.sortOrder}
                                active={pageParam.sortKey === "sentence"}    
                                onClick={() => onClickSortHead("sentence")}
                    >
                        {intl.formatMessage({ id: "i04016" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={pageParam.sortOrder}
                                active={pageParam.sortKey === "intent_name"}    
                                onClick={() => onClickSortHead("intent_name")}
                    >
                        {intl.formatMessage({ id: "i04017" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((selector) => (
                    <div key={selector.id}
                         role="row"
                         className="selection-table-tr group"
                    >
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <input type="checkbox"
                                   className={classNames(
                                       selectedIds.length ? "opacity-100" : "opacity-0",
                                       "selection-table-checkbox group-interaction"
                                   )}
                                   checked={selectedIds.includes(selector.id)}
                                   onChange={e => markOne(selector.id, e.target.checked)}
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <HighlightText query={selector.sentence}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className={classNames(
                                 "selection-table-td pl-[15px]",
                                 !intentActiveMap[selector.intent_id] && "line-through"
                             )}
                        >
                            <HighlightText query={selector.intent_name}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <Popover>
                                {({ open, close }) => (
                                    <>
                                        <PopoverButton className="selection-table-popover-btn"
                                                       open={open}
                                                       close={close}
                                        />
                                        <PopoverMenu>
                                            <PopoverItem className="selection-table-popover-item"
                                                        onClick={() => setEditingSelector(selector)}
                                            >
                                                <EditIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i04018" })}
                                            </PopoverItem>
                                            <PopoverItem className="selection-table-popover-item text-point-red"
                                                        onClick={() => onClickTableRowDeleteBtn(selector)}
                                            >
                                                <DeleteIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i04019" })}
                                            </PopoverItem>
                                        </PopoverMenu>
                                    </>
                                )}
                            </Popover>
                        </div>
                    </div>
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <SelectionActionBar selectionCount={selectionCount}
                                selectAll={() => markAll(true)}
                                deSelect={() => markAll(false)}
                                remove={onClickActionBarDeleteBtn}
                                download={onClickDownload}
            />
        </div>
    );
};

export default SelectorTable;
